import { useState } from 'react';
import { ChevronDown, Download, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { GALLERY_FAVORITES, GALLERY_DOWNLOAD } from '@/constants/testIds';

const RESOLUTIONS = [
  { key: 'print', value: 'original', label: 'Print (Original)' },
  { key: 'web', value: 'web', label: 'Web' },
  { key: 'social', value: 'social', label: 'Social' },
];

const buttonClass = 'inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold transition-colors disabled:opacity-60';

/**
 * Download action for the current lightbox photo. Offers a resolution menu
 * when the image has web/social variants, otherwise downloads the original.
 */
export const LightboxDownloadButton = ({ image, onDownload }) => {
  const [downloading, setDownloading] = useState(false);
  const hasVariants = Boolean(image?.variants && Object.keys(image.variants).length > 0);

  const handleDownload = async (resolution = 'original') => {
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownload(image, resolution);
    } finally {
      setDownloading(false);
    }
  };

  const icon = downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />;

  if (!hasVariants) {
    return (
      <button type="button" onClick={() => handleDownload('original')} disabled={downloading} aria-label="Download photo" data-testid={GALLERY_FAVORITES.lightboxDownload} className={buttonClass}>
        {icon}
        Download
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" disabled={downloading} aria-label="Download photo — choose resolution" data-testid={GALLERY_FAVORITES.lightboxDownload} className={buttonClass}>
          {icon}
          Download
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
        {RESOLUTIONS.map((r) => (
          <DropdownMenuItem key={r.key} data-testid={GALLERY_DOWNLOAD.resolutionOption(r.key)} onClick={() => handleDownload(r.value)} className="text-xs focus:bg-[hsl(var(--lxra-gold))]/15 focus:text-[hsl(var(--lxra-gold))]">
            {r.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
